import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Logo from "../components/common/Logo";
import { ROUTES } from "../utils/constants";
import firestoreService from "../services/firestoreService";
import emailService from "../services/emailService";
import smsService from "../services/smsService";

const COOLDOWN_SECONDS = 120; // 2 minutes
const LS_KEY = "mirtsew_sms_reset_cooldown"; // localStorage key

/** Read remaining cooldown seconds from localStorage (0 if expired / not set) */
function getRemainingCooldown() {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (!stored) return 0;
    const expiresAt = parseInt(stored, 10);
    const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  } catch {
    return 0;
  }
}

/** Stamp a new cooldown starting now */
function stampCooldown() {
  try {
    localStorage.setItem(LS_KEY, String(Date.now() + COOLDOWN_SECONDS * 1000));
  } catch {}
}

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [method, setMethod] = useState("email"); // "email" | "phone"
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Countdown state — only relevant when method === "phone"
  const [countdown, setCountdown] = useState(() => getRemainingCooldown());

  // Tick the countdown every second
  useEffect(() => {
    if (countdown <= 0) return;
    const id = setInterval(() => {
      setCountdown((prev) => {
        const next = prev - 1;
        return next <= 0 ? 0 : next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [countdown]);

  // When switching back to phone method, re-read the cooldown
  useEffect(() => {
    if (method === "phone") {
      setCountdown(getRemainingCooldown());
    }
  }, [method]);

  const formatCountdown = useCallback((secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const val = input.trim();
    if (!val) {
      setError(
        method === "email" ? "Email is required." : "Phone number is required.",
      );
      return;
    }

    // Guard: don't allow SMS send during cooldown
    if (method === "phone" && countdown > 0) {
      setError(
        `Please wait ${formatCountdown(countdown)} before requesting another SMS.`,
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      let userEmail = null;
      let userPhone = null;

      if (method === "email") {
        const account = await firestoreService.getUserAccount(val);
        if (account) {
          userEmail = val;
          const reg = await firestoreService.getRegistrationByEmail(val);
          userPhone = reg?.userInfo?.mobileNumber || null;
        }
      } else {
        // Phone method — look up registration by phone
        const reg = await firestoreService.getRegistrationByPhone(val);
        if (reg) {
          userEmail = reg.userInfo?.email;
          userPhone = reg.userInfo?.mobileNumber || val;
        } else {
          // No matching registration — show success anyway (don't reveal)
          // Still stamp cooldown so they can't probe by spamming
          stampCooldown();
          setCountdown(COOLDOWN_SECONDS);
          setSent(true);
          return;
        }
      }

      // Always show success — don't reveal account existence
      if (!userEmail) {
        if (method === "phone") {
          stampCooldown();
          setCountdown(COOLDOWN_SECONDS);
        }
        setSent(true);
        return;
      }

      // Create reset token
      const token = await firestoreService.createPasswordResetToken(userEmail);
      const resetLink = `${window.location.origin}/reset-password?token=${token}`;

      if (method === "email") {
        await emailService.sendPasswordResetEmail(userEmail, resetLink);
      } else {
        const targetPhone = userPhone || val;
        const smsText = smsService.buildPasswordResetMessage(resetLink);
        const smsResult = await smsService.send(targetPhone, smsText);
        if (!smsResult.sent && smsResult.status !== "ACCEPTED") {
          console.error("[ForgotPwd] SMS failed:", smsResult.error);
        }
        // Stamp cooldown regardless of SMS success (to prevent spam)
        stampCooldown();
        setCountdown(COOLDOWN_SECONDS);
      }

      setSent(true);
    } catch (err) {
      console.error("Forgot password error:", err);
      setError("Failed to send reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const smsCoolingDown = method === "phone" && countdown > 0;

  return (
    <div className="admin-login-page login-page">
      <div className="page-header">
        <div className="container">
          <Logo size="large" className="page-logo" />
          <h1 className="page-title">Mirt Sew</h1>
          <h2 className="page-title-2">Reset Password</h2>
          <p className="page-description">
            Choose how you'd like to receive your password reset link.
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="login-form-container">
            <div className="login-card">
              <div className="card-header">
                <h2 className="card-title">Forgot Password</h2>
              </div>
              <div className="card-body">
                {sent ? (
                  <div style={{ textAlign: "center", padding: "1rem 0" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                      {method === "phone" ? "📱" : "📧"}
                    </div>
                    <p
                      style={{
                        color: "var(--color-success)",
                        fontWeight: "500",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Reset link sent!
                    </p>
                    <p
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "0.9rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      If an account is associated with <strong>{input}</strong>,
                      you'll receive a password reset link via{" "}
                      <strong>{method === "phone" ? "SMS" : "email"}</strong>.
                      The link expires in <strong>10 minutes</strong>.
                    </p>
                    <button
                      type="button"
                      className="submit-button enabled"
                      onClick={() => navigate(ROUTES.ADMIN_LOGIN)}
                    >
                      Back to Login
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="login-form"
                    noValidate
                  >
                    {error && (
                      <div
                        style={{
                          color: "var(--color-danger)",
                          background: "var(--color-danger-bg)",
                          padding: "0.75rem",
                          borderRadius: "0.375rem",
                          marginBottom: "1rem",
                          fontSize: "0.875rem",
                        }}
                      >
                        {error}
                      </div>
                    )}

                    {/* Method selector */}
                    <div
                      style={{
                        display: "flex",
                        gap: "0.75rem",
                        marginBottom: "1.25rem",
                      }}
                    >
                      {[
                        {
                          value: "email",
                          label: "📧 Email",
                          desc: "Receive link by email",
                        },
                        {
                          value: "phone",
                          label: "📱 Phone (SMS)",
                          desc: "Receive link by SMS",
                        },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setMethod(opt.value);
                            setInput("");
                            setError("");
                          }}
                          style={{
                            flex: 1,
                            padding: "0.875rem 0.75rem",
                            borderRadius: "10px",
                            border:
                              method === opt.value
                                ? "2px solid var(--color-primary, #3b82f6)"
                                : "1.5px solid var(--border-primary, #d1d5db)",
                            background:
                              method === opt.value
                                ? "rgba(59,130,246,0.07)"
                                : "var(--bg-secondary, #f9fafb)",
                            cursor: "pointer",
                            textAlign: "center",
                            transition: "border-color 0.15s, background 0.15s",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.9375rem",
                              fontWeight: "600",
                              color:
                                method === opt.value
                                  ? "var(--color-primary)"
                                  : "var(--text-primary)",
                            }}
                          >
                            {opt.label}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-secondary)",
                              marginTop: "2px",
                            }}
                          >
                            {opt.desc}
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Input */}
                    <div className="form-field">
                      <label className="form-label">
                        {method === "email" ? "Email Address" : "Phone Number"}
                        <span className="required-indicator"> *</span>
                      </label>
                      <input
                        key={method}
                        type={method === "email" ? "email" : "tel"}
                        className="form-input"
                        placeholder={
                          method === "email"
                            ? "Enter your email address"
                            : "e.g. 0911234567 or 251911234567"
                        }
                        value={input}
                        onChange={(e) => {
                          setInput(e.target.value);
                          setError("");
                        }}
                        disabled={loading || smsCoolingDown}
                        autoComplete={method === "email" ? "email" : "tel"}
                        required
                      />
                    </div>

                    {/* Cooldown banner */}
                    {smsCoolingDown && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          background: "rgba(245,158,11,0.08)",
                          border: "1px solid rgba(245,158,11,0.3)",
                          borderRadius: "0.5rem",
                          padding: "0.75rem 1rem",
                          marginBottom: "1rem",
                          fontSize: "0.875rem",
                          color: "#92400e",
                        }}
                      >
                        <span style={{ fontSize: "1.25rem" }}>⏳</span>
                        <div>
                          <div style={{ fontWeight: "600" }}>
                            Please wait before requesting again
                          </div>
                          <div
                            style={{ fontSize: "0.8125rem", marginTop: "2px" }}
                          >
                            You can send another SMS in{" "}
                            <strong
                              style={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {formatCountdown(countdown)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="form-actions">
                      <button
                        type="submit"
                        className={`submit-button ${input.trim() && !loading && !smsCoolingDown ? "enabled" : "disabled"}`}
                        disabled={!input.trim() || loading || smsCoolingDown}
                      >
                        {loading ? (
                          <>
                            <LoadingSpinner size="small" />
                            <span>Sending...</span>
                          </>
                        ) : smsCoolingDown ? (
                          `Resend available in ${formatCountdown(countdown)}`
                        ) : (
                          `Send Reset Link via ${method === "phone" ? "SMS" : "Email"}`
                        )}
                      </button>
                    </div>

                    <div style={{ textAlign: "center", marginTop: "1rem" }}>
                      <button
                        type="button"
                        onClick={() => navigate(ROUTES.ADMIN_LOGIN)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--color-primary)",
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontSize: "0.875rem",
                        }}
                      >
                        Back to Login
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
